import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchBerichte,
  fetchBerichtHtml,
  generiereBericht,
  fetchStatus,
  fetchStrategie,
  freezeStrategie,
  waehleBericht,
  selectBerichte,
  selectBerichteStatus,
  selectGewaehlteBerichtId,
  selectBerichtHtml,
  selectBerichtHtmlStatus,
  selectJobs,
  selectJob,
  selectStrategie,
} from 'modules/aktien/features/berichteSlice';
import { formatDatum } from 'core/utils/format';

/** Kleiner Chip fuer eine Aenderungszahl (neu/raus/neue Berichte). */
function AenderungsChip({ label, wert, farbe }) {
  if (!wert) return null;
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 font-mono text-xs ${farbe}`}>
      {label} {wert}
    </span>
  );
}

/** Farbiges Kennzeichen der Berichtsart. */
function TypBadge({ typ, label }) {
  const farbe =
    typ === 'eigen'
      ? 'bg-emerald-500/10 text-emerald-700'
      : 'bg-indigo-500/10 text-indigo-700';
  return (
    <span className={`rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide ${farbe}`}>
      {label || typ}
    </span>
  );
}

/** Ein anklickbarer Eintrag in der Berichtsliste. */
function BerichtKarte({ bericht, aktiv, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded-xl border p-4 text-left transition-colors ${
        aktiv
          ? 'border-ink bg-ink/[0.04]'
          : 'border-line bg-paper-soft hover:bg-ink/[0.03]'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-ink">{formatDatum(bericht.datum)}</span>
        <TypBadge typ={bericht.typ} label={bericht.typLabel} />
      </div>
      <div className="mt-1 text-xs text-ink-muted">
        {bericht.titelAnzahl} Titel
        {bericht.datenstand ? ` · Datenstand ${formatDatum(bericht.datenstand)}` : ''}
        {bericht.erstlauf ? ' · Erstlauf' : ''}
      </div>
      <div className="mt-2 flex flex-wrap gap-1">
        <AenderungsChip label="neu" wert={bericht.aenderungen?.neu} farbe="bg-gain/10 text-gain" />
        <AenderungsChip label="raus" wert={bericht.aenderungen?.raus} farbe="bg-loss/10 text-loss" />
        <AenderungsChip
          label="Berichte"
          wert={bericht.aenderungen?.neueBerichte}
          farbe="bg-paper-sunken text-ink-muted"
        />
      </div>
    </button>
  );
}

/** Zeigt den gewaehlten Bericht als sandboxed iframe (HTML aus dem Service). */
function BerichtAnzeige({ id }) {
  const html = useSelector(selectBerichtHtml(id));
  const status = useSelector(selectBerichtHtmlStatus);

  if (!id) {
    return (
      <div className="flex h-full items-center justify-center text-ink-muted">
        Wähle links einen Bericht.
      </div>
    );
  }
  if (!html) {
    return (
      <div className="flex h-full items-center justify-center text-ink-muted">
        {status === 'failed' ? 'Bericht konnte nicht geladen werden.' : 'Lade Bericht…'}
      </div>
    );
  }
  return (
    <iframe
      title={`Bericht ${id}`}
      srcDoc={html}
      sandbox=""
      className="h-full w-full rounded-xl border border-line bg-white"
    />
  );
}

/** Kleiner rotierender Spinner. */
function Spinner() {
  return (
    <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-ink/30 border-t-ink" />
  );
}

/** Status-/Fortschrittszeile eines Hintergrund-Jobs (laufend oder Endstand). */
function JobStatusZeile({ job, label }) {
  if (!job) return null;
  const fehler = job.ok === false;
  const abgelehnt = fehler && (job.message || '').startsWith('Abgelehnt');
  const farbe = job.running
    ? 'text-ink-muted'
    : fehler
    ? abgelehnt
      ? 'text-amber-600'
      : 'text-loss'
    : 'text-gain';
  return (
    <div className={`flex items-center gap-2 text-xs ${farbe}`}>
      {job.running ? <Spinner /> : <span>{fehler ? (abgelehnt ? '⚠' : '✕') : '✓'}</span>}
      <span className="font-medium">{label}:</span>
      <span className="truncate">{job.running ? job.phase : job.message || job.phase}</span>
    </div>
  );
}

/** Ein Erzeugen-Button je Berichtsart mit Fortschrittszeile darunter. */
function ErzeugenButton({ label, job, disabled, onClick }) {
  const laeuft = job?.running;
  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="rounded-lg bg-ink px-3 py-2 text-sm font-medium text-paper transition-colors hover:bg-ink/90 disabled:opacity-50"
      >
        {laeuft ? `${label} …` : `${label} neu`}
      </button>
      <JobStatusZeile job={job} label={label} />
    </div>
  );
}

/** Zeigt die hinterlegte Strategie und erlaubt das Validieren (freeze). */
function StrategiePanel({ disabled, onFreeze }) {
  const strategie = useSelector(selectStrategie);
  const freezeJob = useSelector(selectJob('freeze'));

  const beschreibung = !strategie?.vorhanden
    ? 'Noch keine Strategie hinterlegt (Startstrategie wird genutzt).'
    : strategie.validiert
    ? `Validiert · Rev ${strategie.revision} · ${strategie.faktoren?.length ?? 0} Faktoren` +
      (strategie.oosIc != null ? ` · OOS-IC ${strategie.oosIc.toFixed(3)}` : '')
    : 'Unvalidierte Startstrategie (accruals/delta_cash/asset_growth).';

  return (
    <div className="rounded-lg border border-line bg-paper-soft p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-medium uppercase tracking-wide text-ink-muted">
            US-Strategie
          </div>
          <div className="truncate text-sm text-ink">{beschreibung}</div>
        </div>
        <button
          type="button"
          onClick={onFreeze}
          disabled={disabled}
          className="shrink-0 rounded-lg border border-ink/20 bg-paper px-3 py-2 text-sm font-medium text-ink transition-colors hover:bg-ink/[0.04] disabled:opacity-50"
        >
          {freezeJob?.running ? 'Validiere …' : 'Strategie validieren'}
        </button>
      </div>
      <div className="mt-1">
        <JobStatusZeile job={freezeJob} label="Validierung" />
      </div>
    </div>
  );
}

/**
 * Bereich „Berichte": Wochenberichte je Art getrennt erzeugbar, mit
 * Live-Fortschritt. Links die Liste (neueste zuerst), rechts der gewaehlte
 * Bericht in einer abgeschotteten iframe. Berichte entstehen zusaetzlich
 * automatisch beim Start des Service und woechentlich.
 */
export default function BerichteView() {
  const dispatch = useDispatch();
  const liste = useSelector(selectBerichte);
  const status = useSelector(selectBerichteStatus);
  const gewaehlteId = useSelector(selectGewaehlteBerichtId);
  const jobs = useSelector(selectJobs);
  const eigenJob = useSelector(selectJob('eigen'));
  const factorlabJob = useSelector(selectJob('factorlab'));
  const freezeJob = useSelector(selectJob('freeze'));

  const anyRunning = jobs.some((j) => j.running);
  // factorlab und freeze belasten beide die SEC -> nie gleichzeitig anstossen.
  const factorlabBusy = Boolean(factorlabJob?.running || freezeJob?.running);

  // Filter nach Berichtsart (alle | eigen | factorlab).
  const [filter, setFilter] = useState('alle');
  const sichtbar = useMemo(
    () => (filter === 'alle' ? liste : liste.filter((b) => b.typ === filter)),
    [liste, filter]
  );

  // Beim Aufruf: Liste, Status und Strategie laden.
  useEffect(() => {
    dispatch(fetchBerichte());
    dispatch(fetchStatus());
    dispatch(fetchStrategie());
  }, [dispatch]);

  // Solange noch kein Bericht da ist, kurz nachladen (Erstlauf laeuft im Hintergrund).
  useEffect(() => {
    if (status === 'succeeded' && liste.length === 0) {
      const t = setTimeout(() => dispatch(fetchBerichte()), 8000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [status, liste.length, dispatch]);

  // Waehrend ein Job laeuft, den Status regelmaessig nachladen.
  useEffect(() => {
    if (!anyRunning) return undefined;
    const t = setInterval(() => dispatch(fetchStatus()), 2500);
    return () => clearInterval(t);
  }, [anyRunning, dispatch]);

  // Beim Uebergang laufend -> fertig: Liste und Strategie aktualisieren.
  const warRunning = useRef(false);
  useEffect(() => {
    if (warRunning.current && !anyRunning) {
      dispatch(fetchBerichte());
      dispatch(fetchStrategie());
    }
    warRunning.current = anyRunning;
  }, [anyRunning, dispatch]);

  // HTML des gewaehlten Berichts nachladen, falls noch nicht im Cache.
  const htmlCache = useSelector((s) => s.berichte.htmlCache);
  useEffect(() => {
    if (gewaehlteId && !htmlCache[gewaehlteId]) {
      dispatch(fetchBerichtHtml(gewaehlteId));
    }
  }, [gewaehlteId, htmlCache, dispatch]);

  const handleGenerieren = (typ) => {
    dispatch(generiereBericht(typ)).then(() => dispatch(fetchStatus()));
  };

  const handleFreeze = () => {
    dispatch(freezeStrategie()).then(() => dispatch(fetchStatus()));
  };

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col">
      <header className="mb-4">
        <h1 className="font-serif text-3xl font-semibold text-ink">Berichte</h1>
        <p className="mt-1 text-sm text-ink-muted">
          „Meine Aktien" fasst deine erfassten Titel zusammen (echte Namen,
          Wochenperformance, Urteil); „US-Faktoren" ist die factorlab-Rangliste auf
          US-Titeln. Beide entstehen zusätzlich automatisch wöchentlich.
        </p>
        <div className="mt-3 grid gap-3 md:grid-cols-[auto_auto_1fr] md:items-start">
          <ErzeugenButton
            label="Meine Aktien"
            job={eigenJob}
            disabled={eigenJob?.running}
            onClick={() => handleGenerieren('eigen')}
          />
          <ErzeugenButton
            label="US-Faktoren"
            job={factorlabJob}
            disabled={factorlabBusy}
            onClick={() => handleGenerieren('factorlab')}
          />
          <StrategiePanel disabled={factorlabBusy} onFreeze={handleFreeze} />
        </div>
      </header>

      {status === 'loading' && <p className="text-ink-muted">Lade Berichte…</p>}
      {status === 'failed' && (
        <p className="rounded-xl border border-dashed border-loss/30 bg-loss/5 p-8 text-center text-loss">
          Die Berichte konnten nicht geladen werden.
        </p>
      )}

      {status === 'succeeded' && liste.length === 0 && (
        <p className="rounded-xl border border-dashed border-line bg-paper-soft p-8 text-center text-ink-muted">
          Noch kein Bericht vorhanden. Der erste Bericht wird gerade erzeugt – das
          dauert einen Moment. Die Liste aktualisiert sich automatisch.
        </p>
      )}

      {status === 'succeeded' && liste.length > 0 && (
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 md:grid-cols-[300px_1fr]">
          {/* Liste */}
          <div className="flex min-h-0 flex-col gap-2">
            {/* Filter nach Berichtsart */}
            <div className="flex gap-1">
              {[
                ['alle', 'Alle'],
                ['eigen', 'Meine Aktien'],
                ['factorlab', 'US-Faktoren'],
              ].map(([wert, label]) => (
                <button
                  key={wert}
                  type="button"
                  onClick={() => setFilter(wert)}
                  className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                    filter === wert
                      ? 'bg-ink text-paper'
                      : 'bg-paper-sunken text-ink-muted hover:text-ink'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="flex flex-col gap-2 overflow-y-auto pr-1">
              {sichtbar.map((b) => (
                <BerichtKarte
                  key={b.id}
                  bericht={b}
                  aktiv={b.id === gewaehlteId}
                  onClick={() => dispatch(waehleBericht(b.id))}
                />
              ))}
            </div>
          </div>
          {/* Anzeige */}
          <div className="min-h-0">
            <BerichtAnzeige id={gewaehlteId} />
          </div>
        </div>
      )}
    </div>
  );
}
